import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import ClinicCard from "@/components/ClinicCard";
import NationalContextSidebar from "@/components/NationalContextSidebar";
import Rubric from "@/components/Rubric";
import { SITE_URL } from "@/lib/site";
import { itemListSchema, articleSchema } from "@/components/schema";
import { BARCELONA_CLINICS } from "@/data/clinics-barcelona";
import { Helmet } from "react-helmet-async";

const PATH = "/best-veneer-clinics-in-barcelona";
const TITLE = "Porcelain veneer clinics in Barcelona — an evaluation";
const DESCRIPTION =
  "Five Barcelona cosmetic-dentistry practices examined on the same twelve-dimension rubric. Each clinic linked to its own website.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function BestVeneerClinicsBarcelona() {
  const itemList = itemListSchema(
    BARCELONA_CLINICS.map((c) => ({
      name: c.name,
      url: `${SITE_URL}${PATH}#${c.slug}`,
      description: c.profile,
    })),
    PATH,
  );

  const article = articleSchema({
    title: TITLE,
    description: DESCRIPTION,
    path: PATH,
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
  });

  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(article)}</script>
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Spain", path: "/best-smile-makeover-clinics-in-spain" },
          { name: "Barcelona", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Comparison &middot; Barcelona"
        title="Porcelain veneer clinics in Barcelona — an evaluation"
        standfirst="Five Barcelona cosmetic-dentistry practices examined on a single rubric, in the same order, linked to their own websites. The city's veneer market is shaped as much by generational family practices as by newer veneer-first studios in Sarrià-Sant Gervasi."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={16}
      />

      <div className="container-prose prose-editorial mt-10">
        <h2>Why Barcelona, as a category</h2>
        <p>
          Barcelona's cosmetic-dentistry market has a distinct shape.
          A cluster of long-tenured family practices — some into their
          second and third generation — sit alongside newer
          veneer-first studios concentrated in Sarrià-Sant Gervasi and
          the Via Augusta corridor. A separate track of hospital- and
          aesthetic-medicine groups offers cosmetic dentistry as part
          of a wider elective-medicine footprint. Each of these models
          produces good veneer work at the high end; each makes
          different trade-offs that the rubric surfaces explicitly.
        </p>
        <p>
          This piece examines five practices across that range,
          evaluated on the same rubric.
        </p>

        <h2>The rubric, applied locally</h2>
        <p>
          The full rubric lives on our{" "}
          <Link to="/methodology">methodology page</Link>. Summarised:
        </p>
        <Rubric variant="summary" />
        <p className="mt-6">
          In Barcelona specifically, the dimensions that differentiate
          most sharply are ceramist proximity (very few Spanish
          practices publicly name the ceramist on the case — Barcelona
          contains one that does), authored clinical doctrine,
          length-of-tenure, and the integration or separation of the
          dental division from a wider aesthetic-medicine group.
        </p>

        <h2>The clinics evaluated</h2>
        <p>
          Profiles below follow the same structure — short description,
          dimension-by-dimension snapshot, and an honest statement of
          where the clinic leads and trails against its peers on this
          list. Every external link goes to the clinic's own website.
        </p>

        {BARCELONA_CLINICS.map((clinic, i) => (
          <ClinicCard key={clinic.slug} clinic={clinic} position={i + 1} />
        ))}

        <h2>What the framework surfaces, on balance</h2>
        <p>
          On <strong>ceramist proximity</strong> — the dimension that
          most cleanly separates studios that treat porcelain as a
          crafted object from those that treat it as a procurement
          item — Barcelona contains one of the few Spanish practices
          that publicly names its ceramist. Readers who weight this
          dimension heavily will find that detail does the work of
          many others.
        </p>
        <p>
          On <strong>length of tenure and the clinician-educator
          profile</strong>, Barcelona's generational family practices
          lead. A clinic whose scientific director teaches the
          aesthetics postgraduate programme that other dentists attend
          is, on the rubric, materially different from a clinic whose
          marketing is excellent.
        </p>
        <p>
          On <strong>hospital-integrated infrastructure</strong>, the
          dental divisions of wider aesthetic-medicine groups lead.
          Patients whose cases have medical complexity, or who want
          surgery and cosmetic dentistry coordinated under one group,
          will find that answer at the hospital-adjacent operations
          rather than at the standalone studios.
        </p>
        <p>
          The point, as always, is not a ranking — it is that a reader
          now knows which dimensions to weight for themselves.
        </p>

        <NationalContextSidebar currentCity="Barcelona" />

        <h2>Related reading</h2>
        <p>
          Readers comparing Barcelona clinics to clinics in other
          Spanish cities should consult the{" "}
          <Link to="/best-smile-makeover-clinics-in-spain">
            national smile-makeover guide
          </Link>
          . If the shortlist also includes Madrid, see our{" "}
          <Link to="/best-veneer-clinics-in-madrid">
            Madrid clinic evaluation
          </Link>
          ; if Valencia,{" "}
          <Link to="/best-veneer-clinics-in-valencia">
            the Valencia evaluation
          </Link>
          . The vocabulary used in the profiles above is developed in{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          {" "}and{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          .
        </p>

        <hr className="rule" />

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>
              2026-04-18 — First publication. Each clinic's name, site
              and public positioning confirmed against the clinic's own
              website at the time of writing.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
